import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Switch,
  FormControlLabel,
  Divider,
  Snackbar,
  IconButton
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { QRCodeCanvas } from 'qrcode.react';
import { useUserAccess } from '../../../context/UserAccessContext';
import { useAdvisorConfig, useAdvisorConfigs, useUpdateAdvisorConfig } from '../../../hooks/useAdvisorConfig';
import { advisorDisplayLabel } from '../../../utils/advisorDisplay';
import { UsefulLink } from '../../../types/advisor';

interface AdvisorProfileEditorProps {
  selectedCustomerId?: string;
  onCustomerIdChange?: (customerId: string) => void;
}

/**
 * Advisor Profile Editor
 *
 * For dashboard-advisors to edit their profile information:
 * - Name, Company, Email, Phone
 * - Advisor Photo
 * - Company Logo
 * - Automation Welcome Message
 *
 * Can be controlled externally via props, or manage its own state.
 */
const AdvisorProfileEditor: React.FC<AdvisorProfileEditorProps> = ({
  selectedCustomerId: controlledCustomerId,
  onCustomerIdChange
}) => {
  const { customerIds } = useUserAccess();
  const [internalCustomerId, setInternalCustomerId] = useState<string>('');
  const [formData, setFormData] = useState({
    automationWelcomeMessage: '',
    calendlyUrl: '',
    customWelcomeMessage: '',
    customThankYouMessage: '',
    sendPresentationToClient: false,
    intakeSendToAdvisor: true,
    notificationEmail: ''
  });
  const [usefulLinks, setUsefulLinks] = useState<UsefulLink[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Use controlled or internal customer ID
  const selectedCustomerId = controlledCustomerId !== undefined ? controlledCustomerId : internalCustomerId;
  const setSelectedCustomerId = (id: string) => {
    if (onCustomerIdChange) {
      onCustomerIdChange(id);
    } else {
      setInternalCustomerId(id);
    }
  };

  // Hooks
  const { config, loading: configLoading, error: configError, refresh } = useAdvisorConfig(
    selectedCustomerId || null,
    true
  );
  const { update, updating, error: updateError } = useUpdateAdvisorConfig();
  // Accessible advisor configs — label the standalone selector with SIPS id + name.
  const { configs: accessibleConfigs } = useAdvisorConfigs();
  const advisorLabel = (customerId: string): string => advisorDisplayLabel(accessibleConfigs, customerId);

  // Initialize selected customer ID (only if not controlled)
  useEffect(() => {
    if (controlledCustomerId === undefined && customerIds.length > 0 && !internalCustomerId) {
      setInternalCustomerId(customerIds[0]);
    }
  }, [customerIds, internalCustomerId, controlledCustomerId]);

  // Load config data into form
  useEffect(() => {
    if (config) {
      setFormData({
        automationWelcomeMessage: config.features?.branding?.automationWelcomeMessage || '',
        calendlyUrl: config.features?.branding?.calendlyUrl || '',
        customWelcomeMessage: config.features?.branding?.customWelcomeMessage || '',
        customThankYouMessage: config.features?.branding?.customThankYouMessage || '',
        sendPresentationToClient: config.features?.emailConfig?.presentationReady?.sendToClient === true,
        intakeSendToAdvisor: config.features?.emailConfig?.intake?.sendToAdvisor !== false,
        notificationEmail: config.features?.emailConfig?.notificationEmail || ''
      });
      setUsefulLinks(config.features?.branding?.usefulLinks || []);
    }
  }, [config]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setSuccessMessage(null);
  };

  const handleSave = async () => {
    if (!selectedCustomerId) return;

    // Validate: if toggle is ON, notification email is required
    if (formData.intakeSendToAdvisor && !formData.notificationEmail.trim()) {
      setSuccessMessage(null);
      return;
    }

    try {
      setSuccessMessage(null);

      // Prepare update payload
      const updates: any = {};

      // Build features object
      updates.features = {};

      // Include branding updates
      updates.features.branding = {
        automationWelcomeMessage: formData.automationWelcomeMessage,
        calendlyUrl: formData.calendlyUrl,
        customWelcomeMessage: formData.customWelcomeMessage,
        customThankYouMessage: formData.customThankYouMessage,
        usefulLinks: usefulLinks.filter(l => l.title && l.url)
      };

      // Include email config — preserve other intake/presentationReady toggles managed in the admin page
      const existingEmail = config?.features?.emailConfig;
      updates.features.emailConfig = {
        ...existingEmail,
        intake: {
          sendToClient: existingEmail?.intake?.sendToClient === true,
          sendToAdvisor: formData.intakeSendToAdvisor,
          sendToSupport: existingEmail?.intake?.sendToSupport === true,
        },
        notificationEmail: formData.intakeSendToAdvisor ? formData.notificationEmail.trim() : ''
      };

      // Only include presentationReady.sendToClient when automation is enabled
      if (config?.automationEnabled === true) {
        updates.features.emailConfig.presentationReady = {
          sendToClient: formData.sendPresentationToClient,
          sendToAdvisor: existingEmail?.presentationReady?.sendToAdvisor === true,
        };
      }

      await update(selectedCustomerId, updates);
      setSuccessMessage('Preferences updated successfully!');
      refresh();
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const wizardBaseUrl = import.meta.env.VITE_CLIENT_INTERVIEW_URL || 'http://localhost:3000';
  const fullLink = `${wizardBaseUrl}/wizard/${selectedCustomerId}`;
  const basicLink = `${wizardBaseUrl}/wizard/${selectedCustomerId}?mode=basic`;

  const handleCopyLink = async (link?: string) => {
    try {
      await navigator.clipboard.writeText(link || fullLink);
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const handleDownloadQRCode = () => {
    const canvas = document.getElementById('qr-code-canvas') as HTMLCanvasElement;
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `qr-code-customer-${selectedCustomerId}.png`;
      link.href = url;
      link.click();
    }
  };


  if (customerIds.length === 0) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        No customer IDs assigned to your account. Contact your administrator.
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      {/* Customer ID Selector (only show if not controlled externally) */}
      {controlledCustomerId === undefined && customerIds.length > 1 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <FormControl fullWidth>
            <InputLabel>Select Advisor Profile</InputLabel>
            <Select
              value={selectedCustomerId}
              onChange={(e) => setSelectedCustomerId(e.target.value)}
              label="Select Advisor Profile"
            >
              {customerIds.map(id => (
                <MenuItem key={id} value={id}>
                  {advisorLabel(id)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
      )}

      {/* Loading State */}
      {configLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {configError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {configError}
        </Alert>
      )}

      {/* Form */}
      {!configLoading && selectedCustomerId && (
        <Paper sx={{ p: 3 }}>
          {/* Customized Link Section */}
          <Typography variant="h6" gutterBottom>
            Your Client Links
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Share these links with clients to access the external intake form. They will see your customized branding and information.
          </Typography>

          {/* Full Intake Link — Basic link is shown alongside when advisor's intake is 'full' or 'demo' (so they have a seminar link too) */}
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', mb: (config?.intakeVersion === 'full' || config?.intakeVersion === 'demo') ? 2 : 4 }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  label={(config?.intakeVersion === 'full' || config?.intakeVersion === 'demo') ? 'Full Interview Link' : 'Client Interview Link'}
                  value={fullLink}
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="outlined"
                  size="small"
                />
                <Button
                  variant="contained"
                  startIcon={<ContentCopyIcon />}
                  onClick={() => handleCopyLink(fullLink)}
                  sx={{ minWidth: '120px' }}
                >
                  Copy
                </Button>
              </Box>
            </Box>

            {/* QR Code */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1
            }}>
              <Box sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'white'
              }}>
                <QRCodeCanvas
                  id="qr-code-canvas"
                  value={fullLink}
                  size={150}
                  level="H"
                  includeMargin={true}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                Scan to open form
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadQRCode}
                sx={{ minWidth: '150px' }}
              >
                Download QR
              </Button>
            </Box>
          </Box>

          {/* Basic / Seminar Intake Link — shown for full and demo intakes only (basic intake already uses the basic flow) */}
          {(config?.intakeVersion === 'full' || config?.intakeVersion === 'demo') && (
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', mb: 4 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    label="Limited Interview Link"
                    value={basicLink}
                    InputProps={{
                      readOnly: true,
                    }}
                    variant="outlined"
                    size="small"
                    helperText="Simplified 4-field intake for seminars and events"
                  />
                  <Button
                    variant="contained"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => handleCopyLink(basicLink)}
                    sx={{ minWidth: '120px' }}
                  >
                    Copy
                  </Button>
                </Box>
              </Box>

              {/* Basic QR Code */}
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1
              }}>
                <Box sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'white'
                }}>
                  <QRCodeCanvas
                    id="qr-code-canvas-basic"
                    value={basicLink}
                    size={150}
                    level="H"
                    includeMargin={true}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Seminar QR
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => {
                    const canvas = document.getElementById('qr-code-canvas-basic') as HTMLCanvasElement;
                    if (canvas) {
                      const url = canvas.toDataURL('image/png');
                      const link = document.createElement('a');
                      link.download = `qr-code-basic-${selectedCustomerId}.png`;
                      link.href = url;
                      link.click();
                    }
                  }}
                  sx={{ minWidth: '150px' }}
                >
                  Download QR
                </Button>
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Automation Preferences */}
          <Typography variant="h6" gutterBottom>
            Automation Preferences
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure case creation and email notification settings
          </Typography>

          <Box sx={{ mt: 2 }}>
            {/* Case titles are a fixed "First Last and First Last Plan" across
                every path (dashboard, webform, API) — no per-advisor prefix. */}

            {/* Send Plan Directly to Client (Automation only) */}
            {config?.automationEnabled === true && (
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.sendPresentationToClient}
                      onChange={(e) => handleInputChange('sendPresentationToClient', e.target.checked)}
                      color="primary"
                      disabled={updating}
                    />
                  }
                  label="Send Plan Directly to Client via Email"
                />
                <Typography variant="caption" display="block" color="text.secondary" sx={{ ml: 4, mt: 0.5 }}>
                  When enabled, the generated presentation will be automatically emailed to the client once it's ready
                </Typography>
              </Box>
            )}

            {/* Email Notifications for Plan Submissions */}
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.intakeSendToAdvisor}
                    onChange={(e) => handleInputChange('intakeSendToAdvisor', e.target.checked)}
                    color="primary"
                    disabled={updating}
                  />
                }
                label="Email me when a plan request is submitted"
              />
              <Typography variant="caption" display="block" color="text.secondary" sx={{ ml: 4, mt: 0.5 }}>
                Receive email confirmations with Case ID when you submit plan requests
              </Typography>
              {formData.intakeSendToAdvisor && (
                <TextField
                  fullWidth
                  label="Notification Email"
                  type="email"
                  value={formData.notificationEmail}
                  onChange={(e) => handleInputChange('notificationEmail', e.target.value)}
                  margin="normal"
                  disabled={updating}
                  required
                  helperText="Email address to receive plan request notifications"
                  size="small"
                  sx={{ ml: 4, maxWidth: 'calc(100% - 32px)' }}
                />
              )}
            </Box>

            {/* Automation Welcome Message */}
            <TextField
              fullWidth
              label="Automation Welcome Message"
              value={formData.automationWelcomeMessage}
              onChange={(e) => handleInputChange('automationWelcomeMessage', e.target.value)}
              margin="normal"
              disabled={updating}
              multiline
              rows={2}
              helperText="Optional message shown to clients on automation flows"
            />

            {/* Calendly URL */}
            <TextField
              fullWidth
              label="Calendly URL"
              value={formData.calendlyUrl}
              onChange={(e) => handleInputChange('calendlyUrl', e.target.value)}
              margin="normal"
              disabled={updating}
              placeholder="https://calendly.com/your-name/30min"
              helperText="Embedded scheduling widget shown on the thank you page"
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Branding */}
          <Typography variant="h6" gutterBottom>
            Client Interview Branding
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Customize the messages clients see on the intake form
          </Typography>

          <TextField
            fullWidth
            label="Custom Welcome Message"
            value={formData.customWelcomeMessage}
            onChange={(e) => handleInputChange('customWelcomeMessage', e.target.value)}
            margin="normal"
            disabled={updating}
            multiline
            rows={2}
            helperText="Shown on the first page of the standard intake form"
          />

          <TextField
            fullWidth
            label="Custom Thank You Message"
            value={formData.customThankYouMessage}
            onChange={(e) => handleInputChange('customThankYouMessage', e.target.value)}
            margin="normal"
            disabled={updating}
            multiline
            rows={2}
            helperText="Shown on the thank you page after form submission"
          />

          <Divider sx={{ my: 3 }} />

          {/* Useful Links */}
          <Typography variant="h6" gutterBottom>
            Useful Links
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Add links shown to clients on the welcome page (e.g., articles, scheduling pages)
          </Typography>

          {usefulLinks.map((link, index) => (
            <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="caption" color="text.secondary">Link {index + 1}</Typography>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setUsefulLinks(prev => prev.filter((_, i) => i !== index))}
                  disabled={updating}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box display="flex" flexDirection="column" gap={1.5}>
                <TextField
                  label="Title"
                  value={link.title}
                  onChange={(e) => {
                    const updated = [...usefulLinks];
                    updated[index] = { ...updated[index], title: e.target.value };
                    setUsefulLinks(updated);
                    setSuccessMessage(null);
                  }}
                  size="small"
                  fullWidth
                  disabled={updating}
                />
                <TextField
                  label="URL"
                  value={link.url}
                  onChange={(e) => {
                    const updated = [...usefulLinks];
                    updated[index] = { ...updated[index], url: e.target.value };
                    setUsefulLinks(updated);
                    setSuccessMessage(null);
                  }}
                  size="small"
                  fullWidth
                  disabled={updating}
                />
                <TextField
                  label="Description (Optional)"
                  value={link.description || ''}
                  onChange={(e) => {
                    const updated = [...usefulLinks];
                    updated[index] = { ...updated[index], description: e.target.value };
                    setUsefulLinks(updated);
                    setSuccessMessage(null);
                  }}
                  size="small"
                  fullWidth
                  disabled={updating}
                />
                <FormControl size="small" fullWidth>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>Icon</Typography>
                  <Select
                    value={link.icon || 'external'}
                    onChange={(e) => {
                      const updated = [...usefulLinks];
                      updated[index] = { ...updated[index], icon: e.target.value as UsefulLink['icon'] };
                      setUsefulLinks(updated);
                      setSuccessMessage(null);
                    }}
                    disabled={updating}
                  >
                    <MenuItem value="calendar">Calendar</MenuItem>
                    <MenuItem value="article">Article</MenuItem>
                    <MenuItem value="media">Media</MenuItem>
                    <MenuItem value="external">External</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Paper>
          ))}

          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              setUsefulLinks(prev => [...prev, { title: '', url: '', description: '', icon: 'external' }]);
              setSuccessMessage(null);
            }}
            disabled={updating}
          >
            Add Link
          </Button>

          {/* Success Message */}
          {successMessage && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {successMessage}
            </Alert>
          )}

          {/* Update Error */}
          {updateError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {updateError}
            </Alert>
          )}

          {/* Save Button */}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={updating ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={updating}
            >
              {updating ? 'Saving...' : 'Save Preferences'}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Success Snackbar for Copy */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          Link copied to clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdvisorProfileEditor;
